// Ícone e tom dos KPIs saem de `VOCABULARIO_KPI` (`src/auditoria/components/ds`).

// src/pages/Inicio.tsx
//
// Fidelidade ao artboard `portal-inicio.dc.html` (UI-2).
//
// Composição do canvas, de cima para baixo: `h1` 24/800 + sublinha de 13px com as
// duas ações do cabeçalho à direita (`.btnsec` + `.btnpri`); faixa de quatro KPIs
// em `1fr` x4 com `border-left:3px` e `.kpi-title` ACIMA do `.kpi-total`
// (`order="title-first"`, escala `sans28` — §5.2); e um card com a grade de
// atalhos `.qa` em três colunas.
//
// Os atalhos apontam só para destinos que existem em `main.tsx` hoje. Os seis do
// canvas estão todos roteados; "Serviços Tomados" é um sétimo que o código já
// tinha e o canvas não desenhou — mantido, porque a rota funciona (registrado no
// mailbox).

import useSWR from 'swr';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';

import {
  DsIcon,
  DsNum,
  DsButton,
  DsKpiCard,
  DsPanelCard,
  DsPageHeader,
} from 'src/auditoria/components/ds';

import type { DsIconName, DsKpiTone, ConceitoKpi } from 'src/auditoria/components/ds';

import { formatCurrency } from './Nfse/types';

// ----------------------------------------------------------------------

type Atalho = {
  titulo: string;
  sub: string;
  path: string;
  /**
   * O canvas desenha nos `.qaicon` glifos que `DsIconName` ainda não tem (nota
   * fiscal, guia, livro de processos, balão de atendimento). Regra §3.4: enquanto
   * o nome não existir, o mais próximo em traço — nunca um `-bold`/`-fill`. Os
   * nomes que faltam estão no mailbox.
   */
  icon: DsIconName;
  /**
   * Só os tons com entrada na paleta do tema: o `.qaicon` pinta o fundo com
   * `theme.vars.palette[tone].main`. O `'neutral'` que `DsKpiTone` ganhou na
   * consolidação da camada compartilhada é cinza sem nó na paleta, então fica
   * de fora aqui — não é tom de atalho.
   */
  tone: Exclude<DsKpiTone, 'neutral'>;
};

/** Os seis `.qa` do artboard, na ordem do canvas, mais Serviços Tomados. */
const ATALHOS: Atalho[] = [
  {
    titulo: 'Emitir NFS-e',
    sub: 'Nova nota avulsa de serviço',
    path: '/nfse/nova',
    icon: 'mais',
    tone: 'primary',
  },
  {
    titulo: 'Consultar NFS-e',
    sub: 'Notas emitidas e recibos',
    path: '/nfse',
    icon: 'buscar',
    tone: 'info',
  },
  {
    titulo: 'Emitir Guia',
    sub: 'Débitos em aberto do cadastro',
    path: '/guias',
    icon: 'baixar',
    tone: 'warning',
  },
  {
    titulo: 'Abrir Protocolo',
    sub: 'Falar com o atendimento fiscal',
    path: '/protocolo/novo',
    icon: 'anexo',
    tone: 'secondary',
  },
  {
    titulo: 'Simular Parcelamento',
    sub: 'Regras e condições ativas',
    path: '/parcelamento/novo',
    icon: 'calendario',
    tone: 'success',
  },
  {
    titulo: 'Plantão Fiscal',
    sub: 'Dúvidas sobre autuações',
    path: '/plantao-fiscal',
    icon: 'info',
    tone: 'error',
  },
  {
    titulo: 'Serviços Tomados',
    sub: 'Notas recebidas de prestadores',
    path: '/servicos-tomados',
    icon: 'exportar',
    tone: 'info',
  },
];

// ----------------------------------------------------------------------

interface PerfilResumo {
  usuario?: { usr_nome?: string; inscricao_municipal?: string | null };
}

export default function InicioPage() {
  const navigate = useNavigate();

  const { data: perfil } = useSWR<PerfilResumo>('/portal-api/usuario/perfil', fetcher);
  // Faixa KPI do canvas: só endpoints já usados pelas outras telas do portal.
  const { data: guias } = useSWR<{
    resumo?: { total: number; quantidade: number };
  }>('/portal-api/guias', fetcher);
  const { data: protocolo } = useSWR<{ processos?: unknown[] }>('/portal-api/protocolo', fetcher);
  const { data: dte } = useSWR<{ naoLidas?: number }>('/portal-api/dte', fetcher);
  const { data: nfse } = useSWR<{ notas?: { data_emissao: string }[] }>(
    '/portal-api/nfse',
    fetcher
  );

  const nome = perfil?.usuario?.usr_nome;
  const inscricao = perfil?.usuario?.inscricao_municipal;
  const ano = new Date().getFullYear();

  const nfseAno = (nfse?.notas ?? []).filter((n) => {
    const t = new Date(n.data_emissao).getTime();
    return Number.isFinite(t) && new Date(t).getFullYear() === ano;
  }).length;

  const saudacao = nome ? `Olá, ${nome}` : 'Olá';

  /**
   * Os quatro KPIs da faixa. Cada um leva à tela que ele resume — todos os `path`
   * abaixo existem em `main.tsx`. Cada card declara só o CONCEITO: ícone e tom
   * saem de `VOCABULARIO_KPI`, a mesma tabela que o app do fisco lê.
   */
  const kpis: {
    titulo: string;
    valor: string;
    sub: string;
    conceito: ConceitoKpi;
    path: string;
  }[] = [
    {
      titulo: 'Guias em aberto',
      valor: formatCurrency(guias?.resumo?.total ?? 0),
      sub: `${guias?.resumo?.quantidade ?? 0} guia(s) em aberto`,
      conceito: 'dinheiro-em-aberto',
      path: '/guias',
    },
    {
      titulo: 'Protocolos ativos',
      valor: String(protocolo?.processos?.length ?? 0),
      sub: 'processos em acompanhamento',
      conceito: 'processos',
      path: '/protocolo',
    },
    {
      titulo: 'Notificações não lidas',
      valor: String(dte?.naoLidas ?? 0),
      sub: 'na caixa de entrada (DTE)',
      conceito: 'caixa-entrada',
      path: '/notificacoes',
    },
    {
      titulo: `NFS-e em ${ano}`,
      valor: String(nfseAno),
      sub: 'notas emitidas no ano',
      conceito: 'notas',
      path: '/nfse',
    },
  ];

  return (
    <DashboardContent>
      <DsPageHeader
        title="Início"
        weight={800}
        subtitle={
          <>
            {saudacao} · ações rápidas e pendências do contribuinte
            {inscricao ? ` · inscrição ${inscricao}` : ''}
          </>
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <DsButton tone="neutral" size="tall" onClick={() => navigate('/notificacoes')}>
              Ver notificações
            </DsButton>
            <DsButton size="tall" icon="mais" onClick={() => navigate('/nfse/nova')}>
              Emitir NFS-e
            </DsButton>
          </Box>
        }
      />

      {/* Faixa de KPIs: `1fr` x4 no canvas; abaixo de `md` a grade quebra em duas
          colunas e depois numa — comportamento inventado, registrado no mailbox. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            md: 'repeat(4, 1fr)',
          },
          gap: '12px',
          mb: '12px',
        }}
      >
        {kpis.map((kpi) => (
          <DsKpiCard
            key={kpi.titulo}
            title={kpi.titulo}
            total={<DsNum>{kpi.valor}</DsNum>}
            sub={kpi.sub}
            scale="sans28"
            order="title-first"
            conceito={kpi.conceito}
            borderLeft
            onClick={() => navigate(kpi.path)}
          />
        ))}
      </Box>

      <DsPanelCard
        title={saudacao}
        subtitle={
          inscricao
            ? `Ações rápidas do contribuinte · Inscrição municipal ${inscricao}`
            : 'Ações rápidas do contribuinte'
        }
        sx={{ padding: '18px 20px' }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: 'repeat(3, 1fr)',
            },
            gap: '10px',
          }}
        >
          {ATALHOS.map((atalho) => (
            // `.qa`: 14px/16px, borda de 1px e raio 12. Sem o `translateY(-4px)` de
            // antes — nenhum artboard desenha esse deslocamento.
            <ButtonBase
              key={atalho.path}
              onClick={() => navigate(atalho.path)}
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                padding: '14px 16px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                color: 'text.primary',
              }}
            >
              <Box
                sx={(theme) => ({
                  width: 38,
                  height: 38,
                  flex: '0 0 38px',
                  borderRadius: '10px',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'common.white',
                  bgcolor: theme.vars.palette[atalho.tone].main,
                })}
              >
                <DsIcon name={atalho.icon} size={18} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: 13, fontWeight: 700 }}>{atalho.titulo}</Box>
                <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{atalho.sub}</Box>
              </Box>
            </ButtonBase>
          ))}
        </Box>
      </DsPanelCard>
    </DashboardContent>
  );
}
